/**
 * Special Offer Cart Handler
 * Tracks when users come from special-offer collection and modifies cart add behavior
 */

class SpecialOfferCartHandler {
  constructor() {
    this.STORAGE_KEY = 'special_offer_collection';
    this.COLLECTION_HANDLE = window.specialOfferCollectionHandle || null;
    this.init();
  }

  init() {
    // Mark that user is viewing special offers collection
    if (this.isSpecialOfferCollection()) {
      this.markSpecialOfferSession();
    }

    // Intercept cart add events
    this.interceptCartAdd();
  }

  isSpecialOfferCollection() {
    // Check if the collection handle is set (means we're on a special offer collection page)
    return this.COLLECTION_HANDLE !== null && window.location.pathname.includes(`/collections/${this.COLLECTION_HANDLE}`);
  }

  markSpecialOfferSession() {
    // Store in sessionStorage that user came from special offers
    sessionStorage.setItem(this.STORAGE_KEY, 'true');
    sessionStorage.setItem(`${this.STORAGE_KEY}_timestamp`, Date.now().toString());

    // Store the special offer prices for products in this collection
    if (window.specialOfferPrices) {
      sessionStorage.setItem(`${this.STORAGE_KEY}_prices`, JSON.stringify(window.specialOfferPrices));
    }
  }

  isFromSpecialOffer() {
    const marked = sessionStorage.getItem(this.STORAGE_KEY);
    const timestamp = sessionStorage.getItem(`${this.STORAGE_KEY}_timestamp`);

    // Check if marked and less than 30 minutes old
    if (marked && timestamp) {
      const thirtyMinutes = 30 * 60 * 1000;
      return (Date.now() - parseInt(timestamp)) < thirtyMinutes;
    }
    return false;
  }

  clearSpecialOfferSession() {
    sessionStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(`${this.STORAGE_KEY}_timestamp`);
    sessionStorage.removeItem(`${this.STORAGE_KEY}_prices`);
  }

  interceptCartAdd() {
    // Override the standard cart add functionality
    document.addEventListener('submit', (e) => {
      const form = e.target;

      // Check if it's a product form (add to cart form)
      if (form.matches('[action="/cart/add"]') || form.matches('[action*="/cart/add"]')) {
        // Only intercept if user came from special offer collection
        if (this.isFromSpecialOffer()) {
          e.preventDefault();
          this.handleSpecialOfferCartAdd(form);
        }
      }
    });

    // Also intercept AJAX cart adds (for quick add buttons)
    this.interceptFetchCartAdd();
  }

  async handleSpecialOfferCartAdd(form) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1');

    // Get metafield price if available (in cents)
    const metafieldPrice = this.getMetafieldPrice();

    const cartItem = {
      id: variantId,
      quantity: quantity,
      properties: {
        '_special_offer': 'true',
        '_offer_collection': this.COLLECTION_HANDLE
      }
    };

    // Add offer price if available (this will be used by Shopify Function)
    if (metafieldPrice) {
      cartItem.properties['_offer_price'] = metafieldPrice;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [cartItem] })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Special offer item added to cart:', data);

        // Trigger cart refresh (for themes with ajax cart)
        this.triggerCartRefresh();

        // Optionally show success message
        this.showSuccessMessage();
      } else {
        console.error('Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  getMetafieldPrice() {
    // Get the product ID from the page
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return null;

    const productId = productInfo.dataset.productId;
    if (!productId) return null;

    // Get the stored special offer prices from sessionStorage
    const storedPrices = sessionStorage.getItem(`${this.STORAGE_KEY}_prices`);
    if (!storedPrices) return null;

    try {
      const prices = JSON.parse(storedPrices);
      // Return the price for this product if it exists
      return prices[productId] ? prices[productId].toString() : null;
    } catch (error) {
      console.error('Error parsing stored prices:', error);
      return null;
    }
  }

  interceptFetchCartAdd() {
    // Store original fetch
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const [url, options] = args;

      // Check if it's a cart add request
      if (url.includes('/cart/add.js') && this.isFromSpecialOffer()) {
        // Modify the request body to include special offer properties
        if (options && options.body) {
          try {
            const body = JSON.parse(options.body);

            if (body.items) {
              // Add properties to all items
              body.items = body.items.map(item => ({
                ...item,
                properties: {
                  ...item.properties,
                  '_special_offer': 'true',
                  '_offer_collection': this.COLLECTION_HANDLE
                }
              }));
            } else if (body.id) {
              // Single item add
              body.properties = {
                ...body.properties,
                '_special_offer': 'true',
                '_offer_collection': this.COLLECTION_HANDLE
              };
            }

            options.body = JSON.stringify(body);
          } catch (e) {
            console.error('Error modifying cart add:', e);
          }
        }
      }

      return originalFetch(...args);
    };
  }

  triggerCartRefresh() {
    // Trigger custom event for themes with ajax cart drawers
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));

    // For themes using specific cart refresh methods
    if (typeof Shopify !== 'undefined' && Shopify.theme && Shopify.theme.cartDrawer) {
      Shopify.theme.cartDrawer.refresh();
    }
  }

  showSuccessMessage() {
    // You can customize this to match your theme's notification system
    console.log('Item added to cart with special offer properties');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SpecialOfferCartHandler();
  });
} else {
  new SpecialOfferCartHandler();
}
